/* eslint-disable no-restricted-globals */
import Debug from 'debug';

// eslint-disable-next-line new-cap
const debug = Debug('ml:copy');

async function legacyCopyToClipboard(link, forHTML) {
  const copy = (event) => {
    if (!event?.clipboardData?.setData) {
      return;
    }
    if (forHTML) {
      event.clipboardData.setData('text/html', link);
    }
    event.clipboardData.setData('text/plain', link);
    event.preventDefault();
  };

  try {
    document.addEventListener('copy', copy);
    document.execCommand('copy');
    return Promise.resolve(true);
  } catch (err) {
    debug('failed to write to clipboard', err);
  } finally {
    document.removeEventListener('copy', copy);
  }
  return Promise.resolve(false);
}

function selectionHelper(url) {
  const link = document.createElement('div');
  link.innerText = url;
  document.body.appendChild(link);
  const selection = window.getSelection() || new Selection();
  selection.removeAllRanges();

  const range = document.createRange();
  range.selectNode(link);
  selection.addRange(range);

  link.style.display = 'none';
  const html = link.innerHTML;
  document.body.removeChild(link);

  return html;
}

/**
 * Copies the video object to the clipboard as an HTML element, using the animated thumbnail if
 * possible, otherwise falling back to the static image.
 * @param {Object} video - The video object to copy to the clipboard.
 * @param {boolean} video.animated - Flag indicated whether the thumbnail is animated.
 * @param {string?} video.animatedThumbUrl - If the video thumbnail is animated, the URL should
 *        point to the animated image.
 * @param {string} video.fullUrl - Used as the destination for the link. Required.
 * @param {string?} video.thumbUrl - The static thumbnail image to use if the video does not
 *        have an animated thumbnail.
 * @param {string?} video.thumbnail - Same as video.thumbUrl, but some objects translate the
 *        existing property name to this. Used for compatibility.
 * @param {string?} video.true_thumbnail_url - animated, user selected thumbnail, or other custom
 *        thumbnail set).
 * @returns {Promise<boolean>} Returns a promise that will resolve to either true, if the HTML
 * was successfully copied to the clipboard, or false otherwise. If the fullUrl is not provided
 * or the thumbnail URL could not be determined, the copy is aborted and false is returned.
 * @remarks This method uses the older document.execCommand API to copy to the clipboard since
 * not all browsers support the Clipboard API/ClipboardItem interfaces yet and this is the only
 * supported way to get multi-part data to the system clipboard.
 * Currently implemented as an async function even though we don't await in it, to allow for future
 * compatibility with the upcoming Clipboard API's async navigator.clipboard.write method. All
 * return statements in this method return a resolved promise.
 */
async function videoAsHTML({
  animated = false, // deprecated
  animatedThumbUrl = '', // deprecated
  fullUrl = '',
  thumbUrl = '', // deprecated
  thumbnail = '', // deprecated
  trueThumbnailUrl = '',
} = {}) {
  let thumbnailUrl;
  if (trueThumbnailUrl) {
    thumbnailUrl = trueThumbnailUrl;
  } else {
    // deprecated
    thumbnailUrl = animated && !!animatedThumbUrl ? animatedThumbUrl : thumbnail || thumbUrl;
  }

  if (!fullUrl || !thumbnailUrl) {
    return Promise.resolve(false);
  }

  const img = document.createElement('img');
  img.src = thumbnailUrl;
  img.style.maxWidth = '480px';
  img.style.maxHeight = '480px';

  const link = document.createElement('a');
  link.href = fullUrl;
  link.target = '_blank';
  link.appendChild(img);

  document.body.appendChild(link);
  const selection = window.getSelection() || new Selection();
  selection.removeAllRanges();

  const range = document.createRange();
  range.selectNode(link);
  selection.addRange(range);

  const html = link.outerHTML;

  try {
    const result = await legacyCopyToClipboard(html, true);
    return result;
  } catch (err) {
    debug('failed to write thumbnail to clipboard', err);
  } finally {
    document.body.removeChild(link);
  }

  // should this be metriced?
  return Promise.resolve(false);
}

/**
 * Copies a video URL to the clipboard, preferring the shortUrl over the fullUrl.
 * @param {Object} video - The video object to copy to the clipboard.
 * @param {string?} video.fullUrl - The fullUrl of the video, used if the shortUrl is unavailable.
 * @param {string?} video.shortUrl - The shortUrl of the video, preferred.,
 * @returns {Promise<boolean>} - Returns a promise that returns true if the URL was successfully
 * copied to the navigator clipboard, false otherwise.
 * @remarks - This uses the Navigator Clipboard API writeText, which is still in development.
 * Currently only secure contexts can write to the clipboard without permission at this time, so it
 * should work in standard browsers at this time. */
async function videoAsLink({ fullUrl = '', shortUrl = '' } = {}) {
  if (shortUrl) {
    try {
      await navigator.clipboard.writeText(shortUrl);
      return true;
    } catch (err) {
      const html = selectionHelper(shortUrl);
      const result = await legacyCopyToClipboard(html, false);
      return result;
    }
  }

  if (fullUrl) {
    try {
      await navigator.clipboard.writeText(fullUrl);
      return true;
    } catch (err) {
      const html = selectionHelper(fullUrl);
      const result = await legacyCopyToClipboard(html, false);
      return result;
    }
  }

  // should this be metriced?
  debug('failed to write any video link to clipboard');
  return false;
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    const result = await legacyCopyToClipboard(text, false);
    return result;
  }
}

export default {
  videoAsHTML,
  videoAsLink,
  copyText,
};
